import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Select } from "@ngxs/store";
import { concat, Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { AppState } from "src/app/app-state";
import { AdminService } from "src/app/injectable/admin.service";
import { GenericErrorModalService } from "src/app/injectable/generic-error-handling/generic-error-modal.service";
import { SenderAnagraficaUpdateRequest } from "./sender-anagrafica-update-request";
import { CategoryItem } from "./category-item";
import { EditLogoDialogComponent } from "./edit-logo-dialog/edit-logo-dialog.component";
import { EditSenderDialogComponent } from "./edit-sender-dialog/edit-sender-dialog.component";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SenderCategoriesUpdateRequest } from "./sender-categories-update-request";
import { AreaItem } from "./area-item";
import { SenderAreasUpdateRequest } from "./sender-areas-update-request";

@Component({
  selector: "app-client-detail",
  templateUrl: "./client-detail.component.html",
  styleUrls: ["./client-detail.component.scss"],
})
export class ClientDetailComponent implements OnInit, OnDestroy {
  @Select(AppState.categories) categories$: Observable<CategoryItem[]>;

  loading = false;
  sub = false;
  senderDetail: any;
  private subscription: Subscription;
  availableCategories: CategoryItem[];
  availableAreas: any[];

  clientForm = this.fb.group({
    sender: this.fb.group({
      areaCreator: [null],
      chatEnabled: [null],
      contractEndDate: { value: null, disabled: false },
      contractStartDate: { value: null, disabled: false },
      contractStatus: [null],
      createdDate: [null],
      descr: [null],
      descrOpt: [null],
      email: [null],
      firma: [null],
      id: [null],
      lang: [null],
      logo: [null],
      modifiedDate: [null],
      name: [null],
      url: [null],
      username: [null],
    }),
    categories: [[]],
    aree: [[]],
    users: [[]],
  });

  constructor(
    private adminService: AdminService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private genericErrorModalService: GenericErrorModalService
  ) {}

  ngOnInit(): void {
    this.loadInfo();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onBackButtonClick() {
    this.router.navigate(["/clients"]);
  }

  loadInfo() {
    this.subscription = this.route.params.subscribe((params) => {
      this.loading = true;
      const id = +params.id;
      concat(
        this.adminService.getSenderDetail(id).pipe(
          map((value) => {
            this.senderDetail = value;
            this.clientForm.patchValue(value);
          })
        ),
        this.adminService.getAllAreas().pipe(
          map((value) => {
            this.availableAreas = value
              .map((item) => {
                const areaItem: AreaItem = new AreaItem(item);
                if (this.senderDetail.aree.find((x) => x.id == areaItem.id))
                  areaItem.selected = true;
                return areaItem;
              })
              .sort((a, b) => a.areaName.localeCompare(b.areaName));
          })
        ),
        this.categories$.pipe(
          map(
            (value) =>
              (this.availableCategories = value
                .map((item) => {
                  const categoryItem: CategoryItem = new CategoryItem(item);
                  if (
                    this.senderDetail.categories.find(
                      (x) => x.categoryId == categoryItem.categoryId
                    )
                  )
                    categoryItem.selected = true;
                  return categoryItem;
                })
                .sort((a, b) => a.categoryName.localeCompare(b.categoryName)))
          )
        )
      ).subscribe(
        (response) => {
          this.loading = false;
        },
        (err) => {
          this.loading = false;
          this.genericErrorModalService.open(
            "Errore nel caricamento dei dettagli del Sender",
            err
          );
        }
      );
    });
  }

  // onSubmit() {
  //   this.updateCategories();
  //   this.setContractDates();
  //   const formValue = this.clientForm.value;
  //   this.sub = true;
  //   this.adminService.updateSenderDetail(formValue).subscribe(result => {
  //     this.sub = false;;
  //     this.router.navigate(['/clients']);
  //   }, error => {
  //     this.sub = false;
  //     console.error(error);
  //   });
  // }

  /**
   * Aggiorna il campo categories nel clientForm a partire da
   * l'oggetto availableCategories
   */
  updateCategories() {
    const modifiedCategories = [];
    this.availableCategories.forEach((item) => {
      if (item.selected) {
        modifiedCategories.push({
          categoryId: item.categoryId,
          categoryName: item.categoryName,
        });
      }
    });
    this.clientForm.patchValue({
      categories: modifiedCategories,
    });
  }

  /**
   * Formatta nel giusto modo i campi delle date di inizio e scadenza
   * contratto nel clientForm prima dell'invio.
   */
  setContractDates() {
    const inizio: Date = new Date(
      this.clientForm.value.sender.contractStartDate
    );
    const scadenza: Date = new Date(
      this.clientForm.value.sender.contractEndDate
    );
    inizio.setHours(0, 0);
    scadenza.setHours(23, 59);
  }

  onItemClick(item: CategoryItem) {
    item.chosen = !item.chosen;
  }

  onSwitchClick() {
    this.availableCategories = this.availableCategories.map((item) => {
      if (item.chosen) item.selected = !item.selected;
      item.chosen = false;
      return item;
    });
  }

  onAreasSwitchClick() {
    this.availableAreas = this.availableAreas.map((item) => {
      if (item.chosen) item.selected = !item.selected;
      item.chosen = false;
      return item;
    });
  }

  onEditSenderButtonClick() {
    const formValue = this.clientForm.value;
    const dialogRef = this.dialog.open(EditSenderDialogComponent, {
      data: {
        name: formValue.sender.name,
        descr: formValue.sender.descr,
        descrOpt: formValue.sender.descrOpt,
        email: formValue.sender.email,
        firma: formValue.sender.firma,
        contractEndDate: formValue.sender.contractEndDate,
        contractStartDate: formValue.sender.contractStartDate,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.clientForm.patchValue({
          sender: result,
        });
      }
    });
  }

  onEditLogoButtonClick() {
    const formValue = this.clientForm.value;
    const dialogRef = this.dialog.open(EditLogoDialogComponent, {
      data: {
        logo: formValue.sender.logo,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.clientForm.patchValue({
          sender: result,
        });
      }
    });
  }

  saveAnagraficaButtonClick() {
    const senderAnagrafica: SenderAnagraficaUpdateRequest = {
      id: this.clientForm.value.sender.id,
      username: this.clientForm.value.sender.username,
      name: this.clientForm.value.sender.name,
      lang: this.clientForm.value.sender.lang,
      logo: this.clientForm.value.sender.logo,
      areaCreator: this.clientForm.value.sender.areaCreator,
      contractStatus: this.clientForm.value.sender.getContractStatus,
      contractEndDate: this.clientForm.value.sender.contractEndDate,
      descr: this.clientForm.value.sender.descr,
      email: this.clientForm.value.sender.email,
      chatEnabled: this.clientForm.value.sender.chatEnabled,
      contractStartDate: this.clientForm.value.sender.contractStartDate,
      descrOpt: this.clientForm.value.sender.descrOpt,
      url: this.clientForm.value.sender.url,
      firma: this.clientForm.value.sender.firma,
      createdDate: this.clientForm.value.sender.createdDate,
      modifiedDate: this.clientForm.value.sender.modifiedDate,
    };
    this.loading = true;
    this.adminService.updateSenderAnagrafica(senderAnagrafica).subscribe(
      (response) => {
        this.openSnackBar(
          "Anagrafica del Sender salvata con successo.",
          "Chiudi"
        );
        this.loading = false;
      },
      (err) => {
        console.error("Si è verificato un errore", err);
        this.genericErrorModalService.open(
          "Errore durante il salvataggio dell'anagrafica del Sender.",
          err
        );
        this.loading = false;
      }
    );
  }

  saveCategoriesButtonClick() {
    const categories = this.availableCategories
      .filter((item) => item.selected)
      .map((item) => {
        return {
          categoryId: item.categoryId,
          categoryCode: item.categoryName,
        };
      });
    const senderCategories: SenderCategoriesUpdateRequest = {
      idSender: this.clientForm.value.sender.id,
      categories,
    };
    this.loading = true;
    this.adminService.updateSenderCategories(senderCategories).subscribe(
      (response) => {
        this.openSnackBar(
          "Categorie del Sender salvate con successo.",
          "Chiudi"
        );
        this.loading = false;
      },
      (err) => {
        console.error("Si è verificato un errore", err);
        this.genericErrorModalService.open(
          "Errore durante il salvataggio delle categorie del Sender.",
          err
        );
        this.loading = false;
      }
    );
  }

  saveAreasButtonClick() {
    const areas = this.availableAreas
      .filter((item) => item.selected)
      .map((item) => {
        return {
          id: item.id,
          areaName: item.areaName,
        };
      });
    const senderAreas: SenderAreasUpdateRequest = {
      idSender: this.clientForm.value.sender.id,
      areas,
    };
    this.loading = true;
    this.adminService.updateSenderAreas(senderAreas).subscribe(
      (response) => {
        this.openSnackBar("Aree del Sender salvate con successo.", "Chiudi");
        this.loading = false;
      },
      (err) => {
        console.error("Si è verificato un errore", err);
        this.genericErrorModalService.open(
          "Errore durante il salvataggio delle aree del Sender.",
          err
        );
        this.loading = false;
      }
    );
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 3500,
      panelClass: "custom-snackbar",
    });
  }
}
